'use client';

import React, { useState, useEffect, useContext } from 'react';
import { supabase } from '../../supabaseClient';
import { AuthContext } from '../../context/AuthContext';
import { PencilIcon } from '@heroicons/react/24/outline';

export default function Account() {
  const { user } = useContext(AuthContext);
  const [profile, setProfile] = useState({
    first_name: '',
    last_name: '',
  });
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return;
      try {
        const { data, error } = await supabase
          .from('profile')
          .select('first_name, last_name')
          .eq('id', user.id)
          .single();

        if (error) throw error;

        setProfile({
          first_name: data.first_name || '',
          last_name: data.last_name || '',
        });
      } catch (err) {
        console.error('Error fetching profile:', err.message);
      }
    };

    fetchProfile();
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfile((prevProfile) => ({
      ...prevProfile,
      [name]: value,
    }));
  };

  const handleToggleEditSave = () => {
    if (editing) {
      handleSave();
    } else {
      setEditing(true);
      setSuccessMessage('');
      setErrorMessage('');
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setSuccessMessage('');
    setErrorMessage('');
    try {
      const { error } = await supabase
        .from('profile')
        .update({
          first_name: profile.first_name,
          last_name: profile.last_name,
        })
        .eq('id', user.id);
      if (error) throw error;

      setSuccessMessage('Profile updated successfully!');
      setEditing(false);
    } catch (err) {
      setErrorMessage('Failed to update profile. Please try again.');
      console.error('Error saving profile:', err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 bg-white rounded-lg border">
      {/* Section Header */}
      <div className="pb-5 flex justify-between items-center">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Account Settings</h2>
          <p className="mt-2 max-w-2xl text-sm text-gray-500">
            Review and update your profile information.
          </p>
        </div>
        <div className="ml-4 mt-4 flex shrink-0 gap-x-4">
          <button
            type="button"
            className="relative inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleToggleEditSave}
            disabled={loading}
          >
            {!editing ? (
              <>
                <PencilIcon className="w-5 h-5 mr-2" />
                Edit
              </>
            ) : (
              'Save'
            )}
          </button>
        </div>
      </div>

      {/* Form Fields */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row justify-between items-center">
          <label htmlFor="first_name" className="text-sm font-medium text-gray-700 sm:w-1/3">
            First Name
          </label>
          <input
            id="first_name"
            type="text"
            name="first_name"
            value={profile.first_name}
            onChange={handleInputChange}
            disabled={!editing}
            className="mt-1 sm:mt-0 sm:w-2/3 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm disabled:opacity-60 disabled:bg-gray-200"
          />
        </div>

        <div className="flex flex-col sm:flex-row justify-between items-center">
          <label htmlFor="last_name" className="text-sm font-medium text-gray-700 sm:w-1/3">
            Last Name
          </label>
          <input
            id="last_name"
            type="text"
            name="last_name"
            value={profile.last_name}
            onChange={handleInputChange}
            disabled={!editing}
            className="mt-1 sm:mt-0 sm:w-2/3 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm disabled:opacity-60 disabled:bg-gray-200"
          />
        </div>

        {successMessage && <p className="text-green-500 text-sm">{successMessage}</p>}
        {errorMessage && <p className="text-red-500 text-sm">{errorMessage}</p>}
      </div>
      {/* Delete Account Section */}
      <div className="mt-6 border-t pt-5">
        <p className="text-sm text-gray-700 mb-4">
          Want to delete your account? Click below to begin.
          </p>
        <button
          type="button"
          className="w-sm inline-flex justify-center rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={loading}
        >
          Delete Account
        </button>
      </div>
    </div>
  
  );
}